import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Snackbar,
    TextField,
    Typography,
    Alert,
    useTheme,
    useMediaQuery
} from '@mui/material';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { API_ENDPOINTS } from '../config/api';

// Declare window.snap for TypeScript
declare global {
    interface Window {
        snap: any;
    }
}

const MEMBER_PRICES: Record<'Basic' | 'Elite', number> = {
    Basic: 150000,
    Elite: 280000
};

function getScheduleCountForMemberType(memberType: 'Basic' | 'Elite') {
    return memberType === 'Elite' ? 20 : 5;
}

function getHariFromTanggal(tanggal: string) {
    if (!tanggal) return '';
    const [tgl, bulan, tahun] = tanggal.split(' ');
    const bulanMap: { [key: string]: number } = {
        'Januari': 0, 'Februari': 1, 'Maret': 2, 'April': 3, 'Mei': 4, 'Juni': 5,
        'Juli': 6, 'Agustus': 7, 'September': 8, 'Oktober': 9, 'November': 10, 'Desember': 11
    };
    const bulanIdx = bulanMap[bulan as keyof typeof bulanMap];
    if (bulanIdx === undefined) return tanggal;
    const date = new Date(Number(tahun), bulanIdx, Number(tgl));
    return date.toLocaleDateString('id-ID', { weekday: 'long' });
}

const Member: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const [user, setUser] = useState<any>(storedUser);
    const [gors, setGors] = useState<any[]>([]);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [bookingMode, setBookingMode] = useState<'pilih_jadwal_sendiri' | 'main_dadakan'>('pilih_jadwal_sendiri');
    type LevelBermain = 'Prelevel' | 'Beginner' | 'Intermediate' | 'Advanced';
    const [levelBermain, setLevelBermain] = useState<LevelBermain>(() => {
        // Try to read from lastBooking first (consistent with other components)
        const lastBooking = localStorage.getItem('lastBooking');
        if (lastBooking) {
            const level = JSON.parse(lastBooking).level;
            if (level === 'Prelevel' || level === 'Beginner' || level === 'Intermediate' || level === 'Advanced') {
                return level;
            }
        }
        // Fallback to individual key
        const savedLevel = localStorage.getItem('levelBermain');
        return savedLevel === 'Prelevel' || savedLevel === 'Beginner' || savedLevel === 'Intermediate' || savedLevel === 'Advanced'
            ? savedLevel
            : 'Beginner';
    });
    const [memberType, setMemberType] = useState<'Basic' | 'Elite'>(() => {
        // Try to read from lastBooking's memberType first
        const lastBooking = localStorage.getItem('lastBooking');
        if (lastBooking) {
            const memberTypeFromLastBooking = JSON.parse(lastBooking).memberType;
            if (memberTypeFromLastBooking === 'Elite' || memberTypeFromLastBooking === 'Basic') {
                return memberTypeFromLastBooking;
            }
        }
        // Fallback to individual key
        const savedMemberType = localStorage.getItem('memberType');
        return savedMemberType === 'Elite' ? 'Elite' : 'Basic';
    });
    const [scheduleSelections, setScheduleSelections] = useState<Array<{ gorId: string; tanggal: string }>>(
        Array.from({ length: getScheduleCountForMemberType('Basic') }, () => ({ gorId: '', tanggal: '' }))
    );
    const [formData, setFormData] = useState({
        nama: storedUser?.displayName || localStorage.getItem('nama') || '',
        email: storedUser?.email || '',
        nomorHP: localStorage.getItem('nomorHP') || ''
    });
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [memberCode, setMemberCode] = useState('');

    useEffect(() => {
        setUser(JSON.parse(localStorage.getItem('user') || '{}'));
        fetchGorList();
    }, []);

    // Simpan data form ke localStorage setiap kali berubah
    useEffect(() => {
        localStorage.setItem('nama', formData.nama);
        localStorage.setItem('nomorHP', formData.nomorHP);
        localStorage.setItem('memberType', memberType);
        localStorage.setItem('levelBermain', levelBermain);
        // Also save to lastBooking for consistency with other components
        localStorage.setItem('lastBooking', JSON.stringify({
            nama: formData.nama,
            nomorHP: formData.nomorHP,
            level: levelBermain,
            memberType: memberType
        }));
    }, [formData.nama, formData.nomorHP, memberType, levelBermain]);

    useEffect(() => {
        const count = getScheduleCountForMemberType(memberType);
        setScheduleSelections((prev) => {
            const next = [...prev];
            if (next.length > count) {
                return next.slice(0, count);
            }
            while (next.length < count) {
                next.push({ gorId: '', tanggal: '' });
            }
            return next;
        });
    }, [memberType]);

    const handleScheduleSelectionChange = (index: number, field: 'gorId' | 'tanggal', value: string) => {
        setScheduleSelections((prev) =>
            prev.map((item, idx) => {
                if (idx !== index) return item;
                if (field === 'gorId') {
                    const selected = gors.find((gor) => gor.id === value);
                    return {
                        gorId: value,
                        tanggal: selected?.tanggalTersedia?.[0] || ''
                    };
                }
                return {
                    ...item,
                    tanggal: value
                };
            })
        );
    };

    const fetchGorList = async () => {
        try {
            const response = await axios.get(API_ENDPOINTS.GOR);
            setGors(response.data || []);
        } catch (error) {
            console.error('Error fetching GOR list:', error);
            setErrorMessage('Gagal memuat pilihan GOR. Silakan muat ulang halaman.');
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');
        setMemberCode('');

        if (!formData.nama.trim() || !formData.email.trim() || !formData.nomorHP.trim()) {
            setErrorMessage('Nama, email, dan nomor HP wajib diisi.');
            return;
        }

        if (bookingMode === 'pilih_jadwal_sendiri') {
            if (scheduleSelections.some((selection) => !selection.gorId || !selection.tanggal)) {
                setErrorMessage(`Silakan lengkapi semua pilihan GOR dan tanggal untuk ${memberType} (${scheduleSelections.length} jadwal).`);
                return;
            }
        }

        try {
            setLoadingSubmit(true);
            const payload: any = {
                bookingType: bookingMode,
                memberType,
                levelBermain,
                nama: formData.nama.trim(),
                email: formData.email.trim(),
                nomorHP: formData.nomorHP.trim()
            };

            if (bookingMode === 'pilih_jadwal_sendiri') {
                payload.jadwals = scheduleSelections.map((selection) => {
                    const selectedGor = gors.find((gor) => gor.id === selection.gorId);
                    return {
                        tempat: selectedGor?.nama || '',
                        tanggal: selection.tanggal
                    };
                });
            }

            const endpoint = API_ENDPOINTS.MEMBERSHIP;

            const response = await axios.post(endpoint, payload);
            const data = response.data;

            if (data?.success) {
                if (data?.order_id) {
                    // Proceed to payment for both modes
                    setSuccessMessage(bookingMode === 'pilih_jadwal_sendiri'
                        ? 'Booking jadwal berhasil. Mengalihkan ke pembayaran...'
                        : 'Booking membership berhasil. Mengalihkan ke pembayaran...');
                    
                    // Get payment token from backend
                    axios.post(API_ENDPOINTS.PAYMENT, {
                        order_id: data.order_id,
                        gross_amount: MEMBER_PRICES[memberType],
                        nama: formData.nama.trim(),
                        email: formData.email.trim(),
                        ...(bookingMode === 'main_dadakan' ? { memberType } : {}),
                        levelBermain,
                        nomorHP: formData.nomorHP.trim(),
                        finish_redirect_url: `${window.location.origin}/booking-sukses`
                    }).then((paymentResponse) => {
                        if (!paymentResponse.data.token) {
                            console.error('No token in payment response:', paymentResponse.data);
                            setErrorMessage('Gagal mendapatkan token pembayaran. Error: ' + (paymentResponse.data.error || 'Unknown error'));
                            setSuccessMessage('');
                            return;
                        }
                        
                        const snapToken = paymentResponse.data.token;
                        const redirectUrl = paymentResponse.data.redirect_url;
                        console.log('Received snap token:', snapToken.substring(0, 20) + '...');
                        console.log('Midtrans redirect URL:', redirectUrl);
                        localStorage.setItem(`payment_${data.order_id}`, JSON.stringify({
                            snapToken,
                            redirectUrl,
                            orderId: data.order_id,
                            createdAt: Date.now()
                        }));

                        // Always use redirect URL like booking mabar
                        if (redirectUrl) {
                            window.location.href = redirectUrl;
                        } else {
                            console.error('No redirect URL received from Midtrans');
                            setErrorMessage('Gagal mendapatkan URL pembayaran. Silakan coba lagi.');
                            setSuccessMessage('');
                        }
                    }).catch((error) => {
                        console.error('Error getting payment token:', error);
                        const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Gagal memproses pembayaran';
                        const detail = error.response?.data?.detail ? JSON.stringify(error.response.data.detail) : '';
                        setErrorMessage(`${errorMsg}${detail ? '. Detail: ' + detail : ''}. Silakan coba lagi.`);
                        setSuccessMessage('');
                    });
                } else {
                    // For pilih_jadwal_sendiri that doesn't require immediate payment
                    setSuccessMessage('Booking member pilih jadwal sendiri berhasil. Silakan cek sheet dan lanjutkan pembayaran jika diperlukan.');
                }
            } else {
                setErrorMessage(data?.message || 'Gagal menyimpan booking membership.');
            }
        } catch (error: any) {
            console.error('Error submitting member booking:', error);
            setErrorMessage(error?.response?.data?.message || 'Terjadi kesalahan saat memproses booking.');
        } finally {
            setLoadingSubmit(false);
        }
    };

    return (
        <Box sx={{ mt: 2, mb: 6 }}>
            <Helmet>
                <title>Member RaketerS | Booking Mabar</title>
            </Helmet>
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, boxShadow: 4 }}>
                    <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', mb: 2 }}>
                        Booking Member RaketerS
                    </Typography>
                <Typography sx={{ mb: 3, color: 'text.secondary' }}>
                    Pilih jenis member dan lakukan booking membership.
                </Typography>

                {!user?.email ? (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        py: 8,
                        textAlign: 'center'
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'warning.main' }}>
                            🔐 Login Diperlukan
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', maxWidth: 400 }}>
                            Anda perlu login terlebih dahulu untuk melakukan booking membership RaketerS.
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => {
                                localStorage.setItem('redirectAfterLogin', window.location.pathname);
                                navigate('/login');
                            }}
                            sx={{ 
                                fontWeight: 600,
                                textTransform: 'none',
                                px: 4,
                                py: 1.5
                            }}
                        >
                            Login Sekarang
                        </Button>
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={7}>
                            <Paper sx={{ p: 3, borderRadius: 3, background: theme.palette.mode === 'dark' ? '#0b1d33' : '#f4f8ff' }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                                    Membership RaketerS
                                </Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                Data Pemesan
                            </Typography>
                            <TextField
                                fullWidth
                                label="Nama"
                                value={formData.nama}
                                onChange={(e) => setFormData({ ...formData, nama: e.target.value })}
                                sx={{ mb: 2 }}
                            />

                            <FormControl fullWidth sx={{ mb: 3 }}>
                                <InputLabel id="level-pemain-label">Level Pemain</InputLabel>
                                <Select
                                    labelId="level-pemain-label"
                                    label="Level Pemain"
                                    value={levelBermain}
                                    onChange={(e) => setLevelBermain(e.target.value as any)}
                                >
                                    <MenuItem value="Prelevel">Prelevel</MenuItem>
                                    <MenuItem value="Beginner">Beginner</MenuItem>
                                    <MenuItem value="Intermediate">Intermediate</MenuItem>
                                    <MenuItem value="Advanced">Advanced</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                label="Email"
                                value={formData.email}
                                disabled
                                sx={{ mb: 2 }}
                                type="email"
                                helperText="Email akan terisi otomatis dari akun Google Anda"
                            />
                            <TextField
                                fullWidth
                                label="Nomor HP"
                                value={formData.nomorHP}
                                onChange={(e) => setFormData({ ...formData, nomorHP: e.target.value })}
                                sx={{ mb: 2 }}
                                placeholder="081234567890"
                            />

                            <FormControl fullWidth sx={{ mb: 3 }}>
                                <InputLabel id="member-type-label">Jenis/Tipe Membership</InputLabel>
                                <Select
                                    labelId="member-type-label"
                                    label="Jenis/Tipe Membership"
                                    value={memberType}
                                    onChange={(e) => setMemberType(e.target.value as 'Basic' | 'Elite')}
                                >
                                    <MenuItem value="Basic">Basic</MenuItem>
                                    <MenuItem value="Elite">Elite</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
                                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                                    Tipe booking membership
                                </FormLabel>
                                <RadioGroup
                                    value={bookingMode}
                                    onChange={(e) => setBookingMode(e.target.value as any)}
                                >
                                    <FormControlLabel
                                        value="pilih_jadwal_sendiri"
                                        control={<Radio />}
                                        label="Pilih Jadwal Sendiri"
                                    />
                                    <FormControlLabel
                                        value="main_dadakan"
                                        control={<Radio />}
                                        label="Main Dadakan"
                                    />
                                </RadioGroup>
                            </FormControl>

                            <Box sx={{ mb: 3, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', background: theme.palette.background.paper }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                    Harga & Ketentuan (1 bulan = 28 hari)
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    Basic: Rp {MEMBER_PRICES.Basic.toLocaleString('id-ID')} per bulan untuk 5x kedatangan.
                                </Typography>
                                <Typography variant="body2">
                                    Elite: Rp {MEMBER_PRICES.Elite.toLocaleString('id-ID')} per bulan untuk 20x kedatangan.
                                </Typography>
                            </Box>

                            {bookingMode === 'pilih_jadwal_sendiri' && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                        Pilih GOR & Jadwal ({scheduleSelections.length} pilihan)
                                    </Typography>
                                    {scheduleSelections.map((selection, index) => {
                                        const selectedGor = gors.find((gor) => gor.id === selection.gorId);
                                        return (
                                            <Box key={index} sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                                    Pilihan {index + 1}
                                                </Typography>
                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid item xs={12} sm={6}>
                                                        <FormControl fullWidth>
                                                            <InputLabel id={`schedule-gor-label-${index}`}>GOR</InputLabel>
                                                            <Select
                                                                labelId={`schedule-gor-label-${index}`}
                                                                label="GOR"
                                                                value={selection.gorId}
                                                                onChange={(e) => handleScheduleSelectionChange(index, 'gorId', e.target.value)}
                                                            >
                                                                <MenuItem value="">Pilih GOR</MenuItem>
                                                                {gors.map((gor) => (
                                                                    <MenuItem key={gor.id} value={gor.id}>
                                                                        {gor.nama}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <FormControl fullWidth>
                                                            <InputLabel id={`schedule-date-label-${index}`}>Tanggal Main</InputLabel>
                                                            <Select
                                                                labelId={`schedule-date-label-${index}`}
                                                                label="Tanggal Main"
                                                                value={selection.tanggal}
                                                                onChange={(e) => handleScheduleSelectionChange(index, 'tanggal', e.target.value)}
                                                                disabled={!selectedGor?.tanggalTersedia?.length}
                                                            >
                                                                <MenuItem value="">Pilih Tanggal</MenuItem>
                                                                {(selectedGor?.tanggalTersedia || []).map((tanggal: string) => (
                                                                    <MenuItem key={tanggal} value={tanggal}>
                                                                        {getHariFromTanggal(tanggal)}, {tanggal}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}

                            {errorMessage && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {errorMessage}
                                </Alert>
                            )}
                            {successMessage && (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    {successMessage}
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                size="large"
                                disabled={loadingSubmit}
                            >
                                {loadingSubmit ? <CircularProgress size={24} /> : 'Booking Member'}
                            </Button>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <Paper sx={{ p: 3, borderRadius: 3, background: theme.palette.mode === 'dark' ? '#071226' : '#fff' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                                Panduan Member
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                • Pilih Jadwal Sendiri: Anda menentukan GOR dan tanggal sejak awal, lalu booking member akan tercatat di database RaketerS.
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                • Main Dadakan: Anda akan mendapatkan kode member unik otomatis, lalu lakukan booking seperti biasa tiap kali akan mengikuti mabar dan lanjutkan dengan memasukkan kode member. Kode member ini tidak dapat dipakai oleh orang yang berbeda.
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                • Main Dadakan: Login dengan email yang sama ketika melakukan booking dengan kode member.
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                • Pastikan nomor HP terisi dengan benar agar pengelola dapat menghubungi Anda.
                            </Typography>

                            <Box sx={{ mt: 3, p: 2, borderRadius: 2, background: theme.palette.action.hover }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                    Info Harga
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    Basic: Rp {MEMBER_PRICES.Basic.toLocaleString('id-ID')} / bulan (5x kedatangan)
                                </Typography>
                                <Typography variant="body2">Elite: Rp {MEMBER_PRICES.Elite.toLocaleString('id-ID')} / bulan (20x kedatangan)</Typography>
                            </Box>
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                    Syarat & Ketentuan Member
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    Dengan mendaftar sebagai member RaketerS, Anda menyetujui syarat dan ketentuan berikut:
                                </Typography>
                                <ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 18 }}>
                                    <li>Keanggotaan (1 bulan) dihitung selama 28 hari sejak hari ini.</li>
                                    <li>Kode member hanya bisa digunakan oleh pemilik email yang terdaftar pada saat pembuatan kode.</li>
                                    <li>Setiap pemakaian kode akan mengurangi jumlah sisa kedatangan sesuai jenis member.</li>
                                    <li>Jika tanggal booking tidak valid atau lebih dari 28 hari sejak hari ini, kode akan ditolak.</li>
                                    <li><strong>RaketerS berhak mengubah syarat dan ketentuan ini tanpa pemberitahuan sebelumnya.</strong></li>
                                </ul>
                                <Typography variant="caption" color="text.secondary">
                                    Catatan: 1 bulan pada sistem member kami dihitung sebagai 28 hari.
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
                )}

                {memberCode && (
                    <Snackbar
                        open
                        autoHideDuration={10000}
                        onClose={() => setMemberCode('')}
                    >
                        <Alert severity="success" sx={{ width: '100%' }}>
                            Kode member Anda: {memberCode}
                        </Alert>
                    </Snackbar>
                )}

                {errorMessage && (
                    <Snackbar
                        open
                        autoHideDuration={6000}
                        onClose={() => setErrorMessage('')}
                    >
                        <Alert severity="error" sx={{ width: '100%' }}>
                            {errorMessage}
                        </Alert>
                    </Snackbar>
                )}

                {successMessage && (
                    <Snackbar
                        open
                        autoHideDuration={6000}
                        onClose={() => setSuccessMessage('')}
                    >
                        <Alert severity="success" sx={{ width: '100%' }}>
                            {successMessage}
                        </Alert>
                    </Snackbar>
                )}
            </Paper>
            </Box>
        </Box>
    );
};

export default Member;
